import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";
import MarketingHome from "@/pages/MarketingHome";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import DashboardHome from "@/pages/DashboardHome";
import Webinars from "@/pages/Webinars";
import WebinarCreate from "@/pages/WebinarCreate";
import WebinarDetail from "@/pages/WebinarDetail";
import AIStudio from "@/pages/AIStudio";
import Registrants from "@/pages/Registrants";
import Automations from "@/pages/Automations";
import Settings from "@/pages/Settings";
import Integrations from "@/pages/Integrations";
import Analytics from "@/pages/Analytics";
import Team from "@/pages/Team";
import Plans from "@/pages/Plans";
import Domains from "@/pages/Domains";
import Courses from "@/pages/Courses";
import CourseCreate from "@/pages/CourseCreate";
import CourseDetail from "@/pages/CourseDetail";
import LivePanel from "@/pages/LivePanel";
import PublicCourse from "@/pages/PublicCourse";
import LandingPages from "@/pages/LandingPages";
import LandingPageNew from "@/pages/LandingPageNew";
import LandingPageBuilder from "@/pages/LandingPageBuilder";
import LandingWaitlist from "@/pages/LandingWaitlist";
import PublicLanding from "@/pages/PublicLanding";
import Credits from "@/pages/Credits";
import JoinInvite from "@/pages/JoinInvite";
import PublicWebinar from "@/pages/PublicWebinar";
import Portal from "@/pages/Portal";
import CoursePortal from "@/pages/CoursePortal";
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";
import Funnels from "@/pages/Funnels";
import FunnelBuilder from "@/pages/FunnelBuilder";
import PublicFunnelEntry from "@/pages/PublicFunnelEntry";
import Payouts from "@/pages/Payouts";
import AdminPayouts from "@/pages/AdminPayouts";
import AdminLifetimeGrants from "@/pages/AdminLifetimeGrants";
import ThumbnailStudio from "@/pages/ThumbnailStudio";
import ThumbnailEditor from "@/pages/ThumbnailEditor";
import LiveRoom from "@/pages/LiveRoom";
import LiveWatch from "@/pages/LiveWatch";
import PresentationDashboard from "@/pages/PresentationDashboard";
import PresentationNew from "@/pages/PresentationNew";
import PresentationEditor from "@/pages/PresentationEditor";
import BrandKits from "@/pages/BrandKits";
import OnboardingForm from "@/pages/OnboardingForm";
import AdminOnboarding from "@/pages/AdminOnboarding";
import ZoomIntegration from "@/pages/ZoomIntegration";
import DesignsList from "@/pages/DesignsList";
import GraphicDesigner from "@/pages/GraphicDesigner";
import CrmLeads from "@/pages/crm/Leads";
import CrmClients from "@/pages/crm/Clients";
import CrmClientDetail from "@/pages/crm/ClientDetail";
import CrmServices from "@/pages/crm/Services";
import CrmSettings from "@/pages/crm/CrmSettings";

function AppRoutes() {
  const location = useLocation();
  // CRITICAL: Detect session_id in URL fragment during render to prevent race conditions.
  // Do NOT move this into useEffect — useEffect runs after routes have already mounted.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<MarketingHome />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/w/:slug" element={<PublicWebinar />} />
      <Route path="/w/:slug/live" element={<LiveWatch />} />
      <Route path="/c/:slug" element={<PublicCourse />} />
      <Route path="/p/:slug" element={<PublicLanding />} />
      <Route path="/f/:slug" element={<PublicFunnelEntry />} />
      <Route path="/portal/:token" element={<Portal />} />
      <Route path="/course-portal/:token" element={<CoursePortal />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      <Route path="/join/:token" element={<JoinInvite />} />
      <Route path="/onboard/:token" element={<OnboardingForm />} />

      <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<DashboardHome />} />
        <Route path="webinars" element={<Webinars />} />
        <Route path="webinars/new" element={<WebinarCreate />} />
        <Route path="webinars/:id" element={<WebinarDetail />} />
        <Route path="webinars/:id/live-room" element={<LiveRoom />} />
        <Route path="presentations" element={<PresentationDashboard />} />
        <Route path="presentations/new" element={<PresentationNew />} />
        <Route path="presentations/:id/edit" element={<PresentationEditor />} />
        <Route path="brand-kits" element={<BrandKits />} />
        <Route path="ai" element={<AIStudio />} />
        <Route path="registrants" element={<Registrants />} />
        <Route path="automations" element={<Automations />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="team" element={<Team />} />
        <Route path="domains" element={<Domains />} />
        <Route path="plans" element={<Plans />} />
        <Route path="courses" element={<Courses />} />
        <Route path="courses/new" element={<CourseCreate />} />
        <Route path="courses/:id" element={<CourseDetail />} />
        <Route path="live/:id" element={<LivePanel />} />
        <Route path="pages" element={<LandingPages />} />
        <Route path="pages/new" element={<LandingPageNew />} />
        <Route path="pages/:id/edit" element={<LandingPageBuilder />} />
        <Route path="pages/:id/waitlist" element={<LandingWaitlist />} />
        <Route path="funnels" element={<Funnels />} />
        <Route path="funnels/:id" element={<FunnelBuilder />} />
        <Route path="credits" element={<Credits />} />
        <Route path="payouts" element={<Payouts />} />
        <Route path="admin/payouts" element={<AdminPayouts />} />
        <Route path="admin/lifetime-grants" element={<AdminLifetimeGrants />} />
        <Route path="admin/onboarding" element={<AdminOnboarding />} />
        <Route path="crm/leads" element={<CrmLeads />} />
        <Route path="crm/clients" element={<CrmClients />} />
        <Route path="crm/clients/:id" element={<CrmClientDetail />} />
        <Route path="crm/services" element={<CrmServices />} />
        <Route path="crm/settings" element={<CrmSettings />} />
        <Route path="studio" element={<ThumbnailStudio />} />
        <Route path="studio/:id/edit" element={<ThumbnailEditor />} />
        <Route path="zoom" element={<ZoomIntegration />} />
        <Route path="designs" element={<DesignsList />} />
        <Route path="designer" element={<GraphicDesigner />} />
        <Route path="designer/:id" element={<GraphicDesigner />} />
        <Route path="payments" element={<Integrations />} />
        <Route path="templates" element={<Integrations />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" theme="dark" richColors closeButton />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
